import { FC, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Center, Flex, StyleProps, Text, XOutline } from '@bonusly/design-system';

import { InlineSelector } from '../../components/form/search/inline-selector';
import { NominatedGroup } from '../../schemas/nominations';
import { GroupAvatar } from './components/group-avatar';

type GroupNomineeSelectorProps = StyleProps & {
	autoFocus?: boolean;
	onChange: (group: NominatedGroup | null) => void;
	onSearch: (query: string) => Promise<NominatedGroup[]>;
	onBlur?: () => void;
	value: NominatedGroup | null;
	placeholder?: string;
};

// Single-select group picker that mirrors UserUsernameSelector's structure but
// renders group recipients (department / location / manager team). Group-type
// scoping is the caller's responsibility — `onSearch` (typically `useSearchNomineeGroups`)
// is expected to return only groups of the desired dimension.
export const GroupNomineeSelector: FC<GroupNomineeSelectorProps> = ({ autoFocus, onChange, onSearch, onBlur, value, placeholder, ...rest }) => {
	const { t } = useTranslation('nominations', { keyPrefix: 'modal' });

	const handleSearch = async (query: string) => {
		try {
			// Drop the currently-selected group from the menu so it can't be picked twice.
			const results = await onSearch(query);
			return results.filter((group) => group.key !== value?.key);
		} catch (error) {
			console.error('Error searching groups:', error);
			return [];
		}
	};

	return (
		<InlineSelector
			autoFocus={autoFocus}
			aria-label={placeholder || t('groupNomineePlaceholder')}
			emptyState={
				<Center py={4}>
					<Text color="text.softest">{t('groupNomineeEmptyState')}</Text>
				</Center>
			}
			onRemove={() => onChange(null)}
			onSearch={handleSearch}
			onBlur={onBlur}
			onSelect={(group: NominatedGroup) => onChange(group)}
			placeholder={placeholder || t('groupNomineePlaceholder')}
			value={value ? [value] : []}
			limit={1}
			renderSelectedItem={({ isSelected, onRemove, value: group }) => (
				<Flex
					alignItems="center"
					aria-label={t('selectedGroupLabel', { name: group.displayName })}
					as="span"
					bgColor="primary.bgAlpha"
					borderRadius="full"
					color="primary.fgStrong"
					display="inline-flex"
					gap={1}
					px={3}
					py={1}
					_selected={{
						outline: '2px solid',
						outlineColor: 'border.focus',
					}}
					{...(isSelected && {
						'data-selected': true,
					})}
				>
					{group.displayName}
					<Center
						as="button"
						type="button"
						onMouseDown={(e: MouseEvent) => {
							e.preventDefault();
							e.stopPropagation();
							onRemove(e);
						}}
					>
						<XOutline size="xs" />
					</Center>
				</Flex>
			)}
			renderMenuItem={({ isSelected, option: group, onSelect }) => (
				<Box
					as="button"
					type="button"
					aria-label={t('selectGroupLabel', { name: group.displayName })}
					borderRadius="sm"
					cursor="pointer"
					display="block"
					onMouseDown={(e: MouseEvent) => {
						e.preventDefault();
						onSelect(e);
					}}
					p={0}
					transition="background-color 0.2s ease-in-out"
					w="full"
					_hover={{
						bgColor: 'primary.bgAlpha',
					}}
					_selected={{
						bgColor: 'primary.bgAlpha',
					}}
					{...(isSelected && {
						'data-selected': true,
					})}
				>
					<Flex alignItems="center" px={2} py={1}>
						<GroupAvatar groupType={group.groupType} size="sm" mr={2} />

						<Box flex={1} minWidth={0} textAlign="left">
							<Text fontSize="sm" fontWeight="medium" lineHeight={5} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
								{group.displayName}
							</Text>

							<Text color="text.softer" fontSize="xs" lineHeight={4} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
								{t(`groupTypeMemberCount.${group.groupType}`, { count: group.userIds.length })}
							</Text>
						</Box>
					</Flex>
				</Box>
			)}
			{...rest}
		/>
	);
};
